/**
 * The avtale numbers, in ONE place.
 *
 * ## Why this file exists
 *
 * David, 10.08.2026, approving the help centre: «just use existing data for
 * now, we will be tweaking the price page and prices in the near future and
 * throughout the next weeks.»
 *
 * Those two facts together — a help centre that quotes prices, and prices that
 * are about to move repeatedly — are exactly how documentation starts lying.
 * A price typed into an article is a price nobody will remember to update, and
 * the first customer to notice will be reading the article and the checkout
 * page side by side.
 *
 * So the numbers live here, `PricingPage` renders them, and the help centre
 * SUBSTITUTES them into article prose at load time (`lib/help/registry.ts`,
 * `{{price.pro.monthly}}`). Changing a price touches this file and nothing
 * else — not one article, not one translation key.
 *
 * ## What belongs here, and what does not
 *
 * Numbers only. Every word — plan names, feature lists, the currency label —
 * stays in the locale files, because those are translated and these are not.
 * `PlanDef.price` carried that same comment before it moved here, and the rule
 * is unchanged.
 *
 * Stripe price ids stay in `PricingPage` beside the checkout call that uses
 * them: they are not customer-facing copy, they never appear in an article,
 * and the help centre must never learn them.
 */

export type PlanKey = 'starter' | 'pro' | 'enterprise'

export interface PlanDef {
  key: PlanKey
  /** The number itself, never words — words belong in the locale files. */
  price?: string
  /** Monthly price when billed yearly */
  priceYearly?: string
  /** Total annual cost when billed yearly (priceYearly × 12) */
  priceYearlyTotal?: string
  popular?: boolean
}

export const PLAN_DEFS: PlanDef[] = [
  {
    key: 'starter',
    price: '0',
  },
  {
    key: 'pro',
    price: '399',
    priceYearly: '339',
    priceYearlyTotal: '4 068',
    popular: true,
  },
  {
    key: 'enterprise',
  },
]

/**
 * Monthly SMS allowance per tier, mirrored from the SERVER's own constant in
 * `supabase/functions/_shared/entitlements.ts` (`SMS_MONTHLY_QUOTA`).
 *
 * Mirrored rather than imported because an edge function is Deno and this is
 * the browser bundle; there is no shared module between them today. The
 * duplication is deliberate and guarded: `check-help.mjs` re-reads the edge
 * function and fails the build if the two ever disagree, so the copy cannot
 * silently drift the way a remembered number would.
 *
 * `null` = unlimited (Enterprise volume is by agreement and monitored).
 */
export const SMS_MONTHLY_QUOTA: Record<PlanKey, number | null> = {
  starter: 0,
  pro: 100,
  enterprise: null,
}

/** Active-event allowance per tier. `null` = unlimited. Mirrors `useSubscription`. */
export const MAX_ACTIVE_EVENTS: Record<PlanKey, number | null> = {
  starter: 1,
  pro: null,
  enterprise: null,
}

const byKey = (key: PlanKey) => PLAN_DEFS.find((p) => p.key === key)

/**
 * The values an article may interpolate, flattened to the `{{…}}` names the
 * Markdown uses. Kept as a function rather than a const so the shape stays
 * derived from `PLAN_DEFS` instead of being a second list to maintain.
 */
export function pricingTokens(): Record<string, string> {
  const pro = byKey('pro')
  return {
    'price.pro.monthly': pro?.price ?? '',
    'price.pro.yearlyMonthly': pro?.priceYearly ?? '',
    'price.pro.yearlyTotal': pro?.priceYearlyTotal ?? '',
    'quota.sms.pro': String(SMS_MONTHLY_QUOTA.pro),
    'quota.events.starter': String(MAX_ACTIVE_EVENTS.starter),
  }
}

/**
 * The plan a stored tier is sold as. Profiles store `professional`, organisations `pro`, and the
 * pricing page calls both «Pro»; capitalising the key printed «Enterprise» beside a «Pro» badge
 * (03.10.2026 re-score). Names come from `pricing.plans.<key>.name`, never from the key.
 */
export function planKeyForTier(tier: string | null | undefined): PlanKey {
  if (tier === 'enterprise') return 'enterprise'
  if (tier === 'professional' || tier === 'pro') return 'pro'
  return 'starter'
}
